import { Doctor } from '@shared/models/doctor';
import { IdName } from '@shared/models/id-name';
import { PatientModel } from '@shared/models/rx-models/interfaces/patient-model';
import { Procedure } from '@shared/models/procedure';
import { DateOfBirthUtils } from '@shared/utils/date-of-birth-utils';
import type { Maybe } from '@/types/globals';
import {
	BridgeContract,
	CloseReason,
	ContextHeader,
	CustomRxEvent,
	CustomRxEventType,
	DentureDetailValue,
	EventHeader,
	HeaderPlatformType,
	procedureIdToName,
	procedureTypeIdToName,
	RxExtensionCloseData,
	RxInitData,
	SessionHeader,
	shadeSystemFromUserSetting,
	TokenRefreshResponseData,
	toothIdSystemFromUserSetting,
	ToothIndication
} from '../models/lab-broker-event.model';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { getToothNumberById, TeethNumberingSystem, UnitTypes, UnitTypesInBridge } from '@modules/rx-intuitive-teeth-orchestrator/models';

export class LabBrokerEventBuilder {
	static buildRxInitEvent({
		data,
		contextHeader,
		jwt,
		tokenType
	}: {
		data: RxInitData;
		contextHeader: ContextHeader;
		jwt?: string;
		tokenType?: string;
	}): CustomRxEvent<RxInitData> {
		const sessionHeader: SessionHeader | undefined =
			jwt !== undefined ? { Jwt: jwt, ...(tokenType !== undefined ? { tokenType } : {}) } : undefined;

		const event: CustomRxEvent<RxInitData> = {
			EventHeader: LabBrokerEventBuilder.buildEventHeader(CustomRxEventType.RxInit),
			ContextHeader: contextHeader,
			Data: data
		};

		if (sessionHeader !== undefined) {
			event.SessionHeader = sessionHeader;
		}

		return event;
	}

	static buildRxInitData({
		patient,
		procedure,
		procedureType,
		userSettings,
		languageCode,
		teeth,
		bridges,
		dentureDetails,
		defaultUnitType
	}: {
		patient: PatientModel | null;
		procedure: Procedure | undefined;
		procedureType: IdName | undefined;
		userSettings: Maybe<{
			ShadeSystemId?: number;
			ToothId?: number;
			DateFormat?: string;
		}>;
		languageCode: string;
		teeth: Tooth[];
		bridges: { [bridgeIndex: number]: Tooth[] };
		dentureDetails:
			| {
					isUpperJawChecked: boolean;
					isLowerJawChecked: boolean;
					isDentureCopyScan: boolean;
			  }
			| undefined;
		defaultUnitType: UnitTypes;
	}): RxInitData {
		const userNumberingSystem = userSettings?.ToothId ?? TeethNumberingSystem.ADA;
		const toothIdSystemToSend = toothIdSystemFromUserSetting.get(userNumberingSystem) ?? 'ADA';

		return {
			Patient: {
				ChartNumber: patient?.ChartNumber ?? '',
				FirstName: patient?.FirstName ?? '',
				LastName: patient?.LastName ?? '',
				DateOfBirth: patient?.DateOfBirth ? DateOfBirthUtils.format(patient.DateOfBirth, 'YYYY-MM-DD') : undefined
			},
			Procedure: {
				Procedure: procedure?.Id != null ? procedureIdToName.get(procedure.Id) ?? '' : '',
				Type: procedureType?.Id != null ? procedureTypeIdToName.get(procedureType.Id) : undefined
			},
			Teeth: this.buildTeethIndications(teeth, defaultUnitType, userNumberingSystem),
			Bridges: this.buildBridgeContracts(bridges, userNumberingSystem),
			...(dentureDetails !== undefined && { DentureDetails: this.buildDentureDetails(dentureDetails) }),
			UserPrefs: {
				language: languageCode,
				dateFormat: userSettings?.DateFormat ?? 'YYYY-MM-DD',
				toothIdSystem: toothIdSystemToSend,
				shadeSystem:
					(userSettings?.ShadeSystemId != null ? shadeSystemFromUserSetting.get(userSettings.ShadeSystemId) : undefined) ??
					'OTHER'
			}
		};
	}

	static buildContextHeader({
		rxId,
		preparedNewRxId,
		patientGuid,
		doctor,
		companyId,
		companyName,
		platform
	}: {
		rxId: Maybe<string>;
		preparedNewRxId: Maybe<string>;
		patientGuid: string | null;
		doctor: Maybe<Doctor>;
		companyId: Maybe<number>;
		companyName: string | undefined;
		platform: HeaderPlatformType;
	}): ContextHeader {
		return {
			RxId: rxId ?? preparedNewRxId ?? '',
			PatientGuid: patientGuid ?? '',
			Doctor: doctor ? { Id: String(doctor.Id), Name: doctor.Name } : undefined,
			Company: companyId ? { Id: String(companyId), Name: companyName } : undefined,
			Platform: platform
		};
	}

	static buildTokenRefreshResponseEvent({
		contextHeader,
		jwt,
		tokenType,
		success,
		errorCode,
		errorMessage
	}: {
		contextHeader: ContextHeader;
		jwt?: string;
		tokenType?: string;
		success: boolean;
		errorCode?: string;
		errorMessage?: string;
	}): CustomRxEvent<TokenRefreshResponseData> {
		const data: TokenRefreshResponseData = {
			Success: success,
			...(success === false && errorCode != null && errorMessage != null ? { Error: { Code: errorCode, Message: errorMessage } } : {})
		};

		const sessionHeader: SessionHeader | undefined = success && jwt ? { Jwt: jwt, ...(tokenType ? { tokenType } : {}) } : undefined;

		const event: CustomRxEvent<TokenRefreshResponseData> = {
			EventHeader: LabBrokerEventBuilder.buildEventHeader(CustomRxEventType.TokenRefreshResponse),
			ContextHeader: contextHeader,
			Data: data
		};

		if (sessionHeader) {
			event.SessionHeader = sessionHeader;
		}

		return event;
	}

	static buildRxExtensionCloseEvent({
		contextHeader,
		reason,
		details
	}: {
		contextHeader: ContextHeader;
		reason: CloseReason;
		details?: string;
	}): CustomRxEvent<RxExtensionCloseData> {
		const data: RxExtensionCloseData = {
			Reason: reason,
			...(details ? { Details: details } : {})
		};

		return {
			EventHeader: LabBrokerEventBuilder.buildEventHeader(CustomRxEventType.RxExtensionClose),
			ContextHeader: contextHeader,
			Data: data
		};
	}

	private static buildEventHeader(type: CustomRxEventType): EventHeader {
		return {
			Id: crypto.randomUUID(),
			Type: type,
			SchemaVersion: '1.0.0',
			EventTime: new Date().toISOString(),
			publisher: 'iTeroRx'
		};
	}

	private static buildTeethIndications(
		teeth: Tooth[],
		defaultUnitType: UnitTypes,
		teethNumberingSystem: TeethNumberingSystem
	): ToothIndication[] {
		return teeth
			.filter(tooth => (!!tooth.UnitTypeID && tooth.UnitTypeID !== defaultUnitType) || !!tooth.ToothInBridgeTypeID)
			.map(tooth => ({
				ToothNumber: String(tooth.ToothID ? getToothNumberById({ toothId: tooth.ToothID, teethNumberingSystem }) : 0),
				Indication: tooth.BridgeIndex
					? UnitTypesInBridge[tooth.ToothInBridgeTypeID ?? 0] ?? ''
					: UnitTypes[tooth.UnitTypeID ?? 0] ?? ''
			}));
	}

	private static buildBridgeContracts(
		bridgesMap: { [bridgeIndex: number]: Tooth[] },
		teethNumberingSystem: TeethNumberingSystem
	): BridgeContract[] {
		return Object.entries(bridgesMap).map(([bridgeIndex, bridgeTeeth]) => {
			const sorted = [...bridgeTeeth].sort((a, b) => (a.ToothID ?? 0) - (b.ToothID ?? 0));
			const first = sorted[0];
			const last = sorted[sorted.length - 1];

			return {
				BridgeIndex: bridgeIndex,
				FromToothId: String(first.ToothID ? getToothNumberById({ toothId: first.ToothID, teethNumberingSystem }) : 0),
				ToToothId: String(last.ToothID ? getToothNumberById({ toothId: last.ToothID, teethNumberingSystem }) : 0)
			};
		});
	}

	private static buildDentureDetails({
		isUpperJawChecked,
		isLowerJawChecked,
		isDentureCopyScan
	}: {
		isUpperJawChecked: boolean;
		isLowerJawChecked: boolean;
		isDentureCopyScan: boolean;
	}): DentureDetailValue[] | undefined {
		const details: DentureDetailValue[] = [];

		if (isUpperJawChecked) {
			details.push('Upper');
		}

		if (isLowerJawChecked) {
			details.push('Lower');
		}

		if (isDentureCopyScan) {
			details.push('DentureCopyScan');
		}

		return details.length > 0 ? details : undefined;
	}
}
